import { createFileRoute, redirect } from "@tanstack/react-router";
import { GraduationCap } from "lucide-react";
import { z } from "zod";

import { ButtonRouter } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { openProPlusLink } from "~/lib/functions/nova-billing";
import { SUPPORT_EMAIL } from "~/lib/shared/brand";
import { createNoIndexMeta } from "~/lib/shared/meta";
import { BILLING_TERMS } from "~/lib/shared/plan-catalogue";
import { BILLING_PATH } from "~/lib/shared/return-path";

/**
 * The graduate Pro+ link, short enough for a QR code:
 * `/pro-plus`, or `/pro-plus?term=yearly`. A signed-out visitor signs in
 * first (the `_user` layout) and comes back here. A graduate with no NOVA plan
 * goes straight to Stripe Checkout at her graduate price; one who already
 * pays for NOVA goes to the Pro+ plan change. Everyone else is told the link
 * is for graduates.
 */
export const Route = createFileRoute("/_user/pro-plus")({
  staticData: { title: "NOVA Pro+" },
  validateSearch: z.object({
    term: z.enum(BILLING_TERMS).optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ term: search.term }),
  loader: async ({ deps }) => {
    const outcome = await openProPlusLink({ data: { term: deps.term } });
    switch (outcome.kind) {
      case "checkout":
        throw redirect({ href: outcome.url });
      case "plan_change":
        throw redirect({
          to: "/settings/profile/billing/change",
          search: { mode: "upgrade_pro_plus", term: outcome.term },
        });
      case "billing":
        throw redirect({
          to: BILLING_PATH,
          search: { notice: outcome.notice },
        });
      case "not_eligible":
        return null;
    }
  },
  head: () =>
    createNoIndexMeta(
      "NOVA Pro+ – Dream Coach Match",
      "NOVA Pro+ for Business Builder Accelerator graduates",
    ),
  component: NotAGraduate,
  errorComponent: CheckoutFailed,
});

function NotAGraduate() {
  const { user } = Route.useRouteContext();
  return (
    <ProPlusMessage title="This link is for Accelerator graduates">
      <p>
        NOVA Pro+ at the graduate price is for graduates of the Business Builder
        Accelerator. We could not find a graduate record for{" "}
        <span className="font-medium text-gray-900">{user.email}</span>.
      </p>
      <p>
        If you graduated, you may have joined with another email address. Sign
        in with that one, or email{" "}
        <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
          {SUPPORT_EMAIL}
        </a>{" "}
        and we will set it up.
      </p>
    </ProPlusMessage>
  );
}

function CheckoutFailed() {
  return (
    <ProPlusMessage title="We could not open checkout">
      <p>
        Something went wrong on our side. Try again in a moment, or choose NOVA
        Pro+ on your plans page.
      </p>
    </ProPlusMessage>
  );
}

function ProPlusMessage({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="mx-auto mt-10 max-w-lg gap-5">
      <div className="flex items-center gap-2">
        <span className="flex size-8 items-center justify-center rounded-lg bg-purple-100">
          <GraduationCap className="size-4 text-purple-700" />
        </span>
        <h1 className="font-heading text-lg font-medium text-gray-900">
          {title}
        </h1>
      </div>
      <div className="text-text-secondary flex flex-col gap-3 text-sm">
        {children}
      </div>
      <ButtonRouter
        to={BILLING_PATH}
        variant="primary"
        size="sm"
        className="w-fit"
      >
        See your plans
      </ButtonRouter>
    </Card>
  );
}
